import React from 'react';
import Img from './Img';
import { Link } from 'react-router-dom';
import { ArrowRight, Users } from 'lucide-react';
import { hero } from '../mock';
import { usePublications } from '../publications';

export default function Hero() {
  const isVideo = /\.(mp4|webm|mov)(\?|$)/i.test(hero.image);
  const { items: pubs, loading: pubsLoading } = usePublications();
  return (
    <section id="home" className="bg-white pt-16 pb-14 md:pt-20 md:pb-20 border-b border-[#ECEEF1]">
      <div className="container-x grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7">
          <span className="eyebrow">{hero.eyebrow}</span>
          <h1 className="heading-serif text-[#232830] text-[42px] md:text-[58px] leading-[1.05] mt-6">
            {hero.titleStart}{' '}
            <span className="italic text-[#133844] relative inline-block">
              <span className="relative z-10">{hero.titleAccent}</span>
              <span className="absolute left-0 right-0 bottom-1 h-[10px] bg-[#8EE8D8] z-0" aria-hidden="true" />
            </span>{' '}
            {hero.titleEnd}
          </h1>

          <p className="mt-6 text-[#232830] text-[16px] leading-relaxed max-w-2xl">
            {hero.description}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/research"
              data-testid="hero-cta-research"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#133844] text-white text-[13.5px] font-semibold hover:bg-[#0C2A34] transition-colors"
            >
              Explore our research <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/team"
              data-testid="hero-cta-team"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#133844] text-[#133844] text-[13.5px] font-semibold hover:bg-[#D1F9F1] transition-colors"
            >
              <Users className="h-4 w-4" /> Meet the team
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative">
            {isVideo ? (
              <video
                muted
                autoPlay
                loop
                playsInline
                preload="metadata"
                poster="/videos/echo_home_poster.webp"
                aria-label="Research echocardiogram loop — the Research Echo Service of the Cheriyan Group at Cambridge University Hospitals"
                className="w-full h-[280px] md:h-[360px] object-cover border border-[#ECEEF1] bg-black"
              >
                {/* WebM first for Chromium builds without H.264 licence; MP4 fallback for Safari/Edge/production */}
                {hero.image.endsWith('.mp4') && (
                  <source src={hero.image.replace(/\.mp4$/, '.webm')} type="video/webm" />
                )}
                <source src={hero.image} type={hero.image.endsWith('.webm') ? 'video/webm' : 'video/mp4'} />
              </video>
            ) : (
              <Img
                src={hero.image}
                alt=""
                width="800"
                height="360"
                className="w-full h-[280px] md:h-[360px] object-cover border border-[#ECEEF1]"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            )}
            <div className="absolute -bottom-3 -left-3 bg-[#8EE8D8] px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase text-[#133844] font-semibold">
              Cambridge Biomedical Campus
            </div>
          </div>
        </div>
      </div>

      {/* Stat row */}
      {Array.isArray(hero.stats) && hero.stats.length > 0 && (
        <div className="container-x mt-14 md:mt-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 md:gap-y-0 md:divide-x md:divide-[#ECEEF1]">
            {hero.stats.map((s, i) => {
              // Any stat whose label mentions "publications" is bound to the
              // live count from usePublications so it stays in sync with the
              // Publications page. Falls back to the seeded value while loading.
              const isPubs = /publication/i.test(s.label || '');
              const displayValue = isPubs
                ? (pubsLoading || !pubs || pubs.length === 0 ? s.value : String(pubs.length))
                : s.value;
              return (
                <div key={s.label || `hero-stat-${i}`} className="px-2 md:px-6 first:pl-0 md:first:pl-0" data-testid={`hero-stat-${i}`}>
                  <div className="heading-serif text-[#133844] text-[34px] md:text-[42px] leading-none">
                    {displayValue}
                    {s.suffix && <span className="text-[22px] md:text-[26px] text-[#546072] ml-1">{s.suffix}</span>}
                  </div>
                  <div className="mt-2 text-[12.5px] text-[#546072] leading-snug">{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
